import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, AlertCircle, Tv, Star, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// LocalStorage key for favorite team
const FAVORITE_TEAM_KEY = 'shelovesthee_favorite_team';

// Get watch/streaming link based on broadcast network
const getWatchLink = (broadcast) => {
  const network = (broadcast || '').toLowerCase();
  
  if (network.includes('swac tv') || network.includes('swactv')) {
    return 'https://www.amazon.com/gp/video/storefront/ref=atv_hm_hom_c_9zZ8vl_brws_SWAC?contentType=subscription&contentId=swactv';
  }
  if (network.includes('espn+') || network.includes('espn plus')) {
    return 'https://www.espn.com/espnplus/';
  }
  if (network.includes('espn3')) {
    return 'https://www.espn.com/watch/espn3';
  }
  if (network.includes('espn2')) {
    return 'https://www.espn.com/watch/';
  }
  if (network.includes('espn')) {
    return 'https://www.espn.com/watch/';
  }
  if (network.includes('hbcu go') || network.includes('hbcugo')) {
    return 'https://hbcugo.tv/';
  }
  if (network.includes('prime') || network.includes('amazon')) {
    return 'https://www.amazon.com/gp/video/storefront/';
  }
  if (network.includes('nbc') || network.includes('peacock')) {
    return 'https://www.peacocktv.com/';
  }
  if (network.includes('cbs') || network.includes('paramount')) {
    return 'https://www.paramountplus.com/';
  }
  // Default to SWAC TV page on the site
  return '/swac-tv';
};

// Get/Set favorite team from localStorage
const getFavoriteTeam = () => {
  try {
    return localStorage.getItem(FAVORITE_TEAM_KEY) || null;
  } catch {
    return null;
  }
};

const setFavoriteTeam = (teamName) => {
  try {
    if (teamName) {
      localStorage.setItem(FAVORITE_TEAM_KEY, teamName);
    } else {
      localStorage.removeItem(FAVORITE_TEAM_KEY);
    }
  } catch {
    // localStorage not available
  }
};

// Format date for grouping - shorter for mobile
const formatGameDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York'
  });
};

// Format time
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    timeZone: 'America/New_York'
  });
};

// Format week range for selector
const formatWeekLabel = (start, end) => {
  if (!start) return '';
  const startDate = new Date(start + 'T00:00:00');
  const endDate = end ? new Date(end + 'T00:00:00') : startDate;
  const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${startStr} - ${endStr}`;
};

// Single Game Row Component (ESPN-style with enhanced readability)
const GameRow = ({ game, index, favoriteTeam, onToggleFavorite }) => {
  const [away, home] = game.teams || [];
  const isLive = game.state === 'in';
  const isFinal = game.state === 'post';
  
  // Check if this game involves the user's favorite team
  const isFavoriteGame = favoriteTeam && (
    away?.name?.includes(favoriteTeam) || 
    home?.name?.includes(favoriteTeam) ||
    away?.abbreviation === favoriteTeam ||
    home?.abbreviation === favoriteTeam
  );
  
  // Inline styles for clamp-based responsive typography
  const teamNameStyle = { fontSize: 'clamp(18px, 4.8vw, 22px)', lineHeight: 1.3 };
  const scoreStyle = { fontSize: 'clamp(18px, 4.8vw, 22px)', lineHeight: 1.3 };
  const timeStyle = { fontSize: 'clamp(17px, 4.5vw, 21px)', lineHeight: 1.2 };
  const broadcastStyle = { fontSize: 'clamp(11px, 2.8vw, 13px)' };
  
  return (
    <div 
      className={`bg-white border-b border-gray-100 last:border-b-0 ${isFavoriteGame ? 'bg-yellow-50 border-l-4 border-l-yellow-400' : ''}`}
      data-testid={`game-row-${index}`}
    >
      {/* Enhanced Row Layout with more vertical spacing */}
      <div className="flex items-stretch">
        {/* Teams Column */}
        <div className="flex-1 py-3 px-3 min-w-0">
          {/* Away Team Row */}
          <div className="flex items-center justify-between mb-2" data-testid={`away-team-${index}`}>
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <img 
                src={away?.logo} 
                alt=""
                className="w-6 h-6 sm:w-7 sm:h-7 object-contain flex-shrink-0"
                onError={(e) => { e.target.style.opacity = '0'; }}
              />
              <span 
                className={`truncate font-semibold ${away?.winner ? 'font-bold text-[#00204E]' : 'text-gray-800'}`}
                style={teamNameStyle}
              >
                {away?.abbreviation || away?.name?.split(' ').pop() || 'TBD'}
              </span>
              {away?.record && (
                <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">({away.record})</span>
              )}
            </div>
            <span 
              className={`font-bold tabular-nums w-10 text-right ${away?.winner ? 'text-[#00204E]' : 'text-gray-600'}`}
              style={scoreStyle}
            >
              {(isLive || isFinal) ? (away?.score ?? '-') : ''}
            </span>
          </div>
          
          {/* Home Team Row */}
          <div className="flex items-center justify-between" data-testid={`home-team-${index}`}>
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <img 
                src={home?.logo} 
                alt=""
                className="w-6 h-6 sm:w-7 sm:h-7 object-contain flex-shrink-0"
                onError={(e) => { e.target.style.opacity = '0'; }}
              />
              <span 
                className={`truncate font-semibold ${home?.winner ? 'font-bold text-[#00204E]' : 'text-gray-800'}`}
                style={teamNameStyle}
              >
                {home?.abbreviation || home?.name?.split(' ').pop() || 'TBD'}
              </span>
              {home?.record && (
                <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">({home.record})</span>
              )}
            </div>
            <span 
              className={`font-bold tabular-nums w-10 text-right ${home?.winner ? 'text-[#00204E]' : 'text-gray-600'}`}
              style={scoreStyle}
            >
              {(isLive || isFinal) ? (home?.score ?? '-') : ''}
            </span>
          </div>
        </div>
        
        {/* Status Column - Right side divider with vertical centering */}
        <div className="w-[80px] sm:w-[96px] flex-shrink-0 border-l border-gray-200 flex flex-col items-center justify-center py-3 px-2 bg-gray-50/70">
          {isLive ? (
            <div className="text-center" data-testid={`game-status-live-${index}`}>
              <div className="inline-flex items-center gap-1 text-xs font-bold text-red-600 uppercase bg-red-50 px-2 py-1 rounded">
                <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                LIVE
              </div>
              <div className="text-xs text-gray-600 mt-1 leading-tight font-medium">{game.detail}</div>
              {game.broadcast && (
                <a 
                  href={getWatchLink(game.broadcast)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-white bg-red-600 hover:bg-red-700 px-2 py-0.5 rounded transition-colors"
                  data-testid={`watch-live-${index}`}
                >
                  <Tv className="w-2.5 h-2.5" />
                  WATCH
                </a>
              )}
            </div>
          ) : isFinal ? (
            <div className="text-center" data-testid={`game-status-final-${index}`}>
              <span className="text-sm font-bold text-gray-500 uppercase">FINAL</span>
            </div>
          ) : (
            <div className="text-center" data-testid={`game-status-scheduled-${index}`}>
              <div className="font-bold text-[#00204E]" style={timeStyle}>{formatTime(game.date)}</div>
              {game.broadcast && (
                <>
                  <div className="text-gray-500 mt-0.5 flex items-center justify-center gap-1" style={broadcastStyle}>
                    <Tv className="w-3 h-3" />
                    <span className="truncate max-w-[55px]">{game.broadcast.split(',')[0]}</span>
                  </div>
                  <a 
                    href={getWatchLink(game.broadcast)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold text-white bg-[#00204E] hover:bg-[#003366] px-2 py-0.5 rounded transition-colors"
                    data-testid={`watch-btn-${index}`}
                  >
                    <Tv className="w-2.5 h-2.5" />
                    WATCH
                  </a>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {game.article && (
        <Link
          to={game.article.url}
          className="block mt-1 text-xs font-bold text-[#DC2626] hover:underline truncate"
          data-testid={`game-article-link-${index}`}
        >
          Read: {game.article.title || 'Game coverage'} →
        </Link>
      )}
    </div>
  );
};

// Live Alert Banner Component
const LiveAlertBanner = ({ liveGames, onDismiss }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (liveGames.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % liveGames.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [liveGames.length]);
  
  if (!liveGames.length) return null;
  
  const game = liveGames[currentIndex];
  const [away, home] = game.teams || [];
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-3 py-2 shadow-lg"
      data-testid="live-alert-banner"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            <span className="text-xs font-bold uppercase">LIVE</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-bold">{away?.abbreviation || away?.name?.split(' ').pop()}</span>
            <span className="font-bold">{away?.score ?? '-'}</span>
            <span className="text-red-200">@</span>
            <span className="font-bold">{home?.abbreviation || home?.name?.split(' ').pop()}</span>
            <span className="font-bold">{home?.score ?? '-'}</span>
            {game.detail && (
              <span className="text-red-200 text-xs ml-2">{game.detail}</span>
            )}
          </div>
          {liveGames.length > 1 && (
            <span className="text-red-200 text-xs">
              ({currentIndex + 1}/{liveGames.length})
            </span>
          )}
        </div>
        <button 
          onClick={onDismiss}
          className="p-1 hover:bg-red-500 rounded transition-colors"
          aria-label="Dismiss live alert"
          data-testid="dismiss-live-alert"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Main Homepage Scoreboard - Compact ESPN-style
export default function HomeScoreboard({ maxGames = null, showHeader = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [favoriteTeam, setFavoriteTeamState] = useState(getFavoriteTeam);
  const [showLiveAlert, setShowLiveAlert] = useState(true);
  const refreshInterval = useRef(null);
  const currentWeekRef = useRef(null);
  
  // Toggle favorite team
  const handleToggleFavorite = (teamName) => {
    const newFavorite = favoriteTeam === teamName ? null : teamName;
    setFavoriteTeamState(newFavorite);
    setFavoriteTeam(newFavorite);
  };
  
  // Calculate week start (Tuesday-Monday)
  const getWeekStart = useCallback((offset = 0) => {
    const now = new Date();
    const eastern = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const day = eastern.getDay();
    const daysSinceTuesday = (day + 5) % 7;
    const tuesday = new Date(eastern);
    tuesday.setDate(eastern.getDate() - daysSinceTuesday + (offset * 7));
    return tuesday.toISOString().split('T')[0];
  }, []);
  
  const fetchScoreboard = useCallback(async (preserveWeek = false) => {
    try {
      const start = getWeekStart(weekOffset);
      if (!preserveWeek) {
        currentWeekRef.current = start;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/swac-scoreboard?start=${start}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch');
      }
      
      const result = await response.json();
      setData(result);
      setError(result.stale ? 'Cached data' : null);
    } catch (err) {
      console.error('Scoreboard error:', err);
      if (!data) {
        setError('Unable to load');
      }
    } finally {
      setLoading(false);
    }
  }, [weekOffset, getWeekStart, data]);
  
  useEffect(() => {
    setLoading(true);
    fetchScoreboard();
    
    refreshInterval.current = setInterval(() => fetchScoreboard(true), 60000);
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [fetchScoreboard]);
  
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) fetchScoreboard(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchScoreboard]);
  
  // Group games by date, but put favorite team games first
  const allGames = maxGames ? (data?.games || []).slice(0, maxGames) : (data?.games || []);
  
  // Sort games - favorite team games first, then by date
  const sortedGames = [...allGames].sort((a, b) => {
    const aIsFavorite = favoriteTeam && (
      a.teams?.[0]?.name?.includes(favoriteTeam) || 
      a.teams?.[1]?.name?.includes(favoriteTeam) ||
      a.teams?.[0]?.abbreviation === favoriteTeam ||
      a.teams?.[1]?.abbreviation === favoriteTeam
    );
    const bIsFavorite = favoriteTeam && (
      b.teams?.[0]?.name?.includes(favoriteTeam) || 
      b.teams?.[1]?.name?.includes(favoriteTeam) ||
      b.teams?.[0]?.abbreviation === favoriteTeam ||
      b.teams?.[1]?.abbreviation === favoriteTeam
    );
    
    if (aIsFavorite && !bIsFavorite) return -1;
    if (!aIsFavorite && bIsFavorite) return 1;
    return new Date(a.date) - new Date(b.date);
  });
  
  const gamesByDate = {};
  let gameIndex = 0;
  
  sortedGames.forEach(game => {
    const dateKey = formatGameDate(game.date);
    if (!gamesByDate[dateKey]) {
      gamesByDate[dateKey] = [];
    }
    gamesByDate[dateKey].push({ ...game, _index: gameIndex++ });
  });
  
  // Get live games for alert banner
  const liveGames = allGames.filter(g => g.state === 'in');
  
  // Calculate week number (Week 0 is a valid SWAC football week)
  const getWeekNumber = (startDate) => {
    if (!startDate) return 'This Week';
    const seasonStart = new Date('2026-08-25');
    const weekStart = new Date(startDate);
    // No +1 offset - Week 0 is real
    const weekNum = Math.floor((weekStart - seasonStart) / (7 * 24 * 60 * 60 * 1000));
    return weekNum >= 0 ? `Week ${weekNum}` : 'Preseason';
  };
  
  // Count live games
  const liveCount = liveGames.length;
  
  return (
    <>
      {/* Live Alert Banner - Fixed at top when games are in progress */}
      {showLiveAlert && liveGames.length > 0 && (
        <LiveAlertBanner 
          liveGames={liveGames} 
          onDismiss={() => setShowLiveAlert(false)} 
        />
      )}
      
    <div className={`bg-white ${showLiveAlert && liveGames.length > 0 ? 'mt-10' : ''}`} data-testid="home-scoreboard">
      {/* Scoreboard Header */}
      {showHeader && (
        <div className="bg-[#00204E] text-white px-3 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-bold tracking-tight" style={{ fontSize: 'clamp(15px, 4vw, 18px)' }}>SWAC Scores</span>
              {liveCount > 0 && (
                <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  {liveCount} LIVE
                </span>
              )}
              {favoriteTeam && (
                <span className="bg-yellow-500 text-[#00204E] text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  {favoriteTeam}
                </span>
              )}
            </div>
            <Link 
              to="/swac-scoreboard"
              className="text-sm text-gray-300 hover:text-white transition-colors font-medium"
              data-testid="full-scoreboard-link"
            >
              All Scores →
            </Link>
          </div>
        </div>
      )}
      
      {/* Week Selector - Enhanced Typography */}
      <div className="bg-gray-50 border-b border-gray-200 px-3 py-2">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setWeekOffset(w => w - 1)}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            aria-label="Previous week"
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="text-center flex items-center gap-2">
            <span className="font-bold text-[#00204E]" style={{ fontSize: 'clamp(17px, 4.5vw, 21px)' }}>
              {getWeekNumber(data?.start)}
            </span>
            <span className="text-gray-500" style={{ fontSize: 'clamp(14px, 3.5vw, 16px)' }}>
              {formatWeekLabel(data?.start, data?.end)}
            </span>
          </div>
          
          <button
            onClick={() => setWeekOffset(w => w + 1)}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            aria-label="Next week"
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {weekOffset !== 0 && (
          <div className="text-center mt-1">
            <button
              onClick={() => setWeekOffset(0)}
              className="text-sm text-red-600 hover:text-red-700 font-semibold"
              data-testid="return-current-week-btn"
            >
              ← Current Week
            </button>
          </div>
        )}
      </div>
      
      {/* Games List */}
      <div>
        {loading && !data ? (
          <div className="px-3 py-6 text-center text-gray-400 text-sm" data-testid="scoreboard-loading">
            Loading scores...
          </div>
        ) : Object.keys(gamesByDate).length === 0 ? (
          <div className="px-3 py-6 text-center text-gray-400 text-sm" data-testid="no-games">
            No games this week
          </div>
        ) : (
          Object.entries(gamesByDate).map(([date, dateGames]) => (
            <div key={date}>
              {/* Date Header - Enhanced Typography */}
              <div className="bg-gray-100 px-3 py-1.5 border-b border-gray-200">
                <span className="font-bold text-gray-600 uppercase tracking-wide" style={{ fontSize: 'clamp(14px, 3.5vw, 16px)' }}>
                  {date}
                </span>
              </div>
              
              {/* Games for this date */}
              {dateGames.map(game => (
                <GameRow 
                  key={game.id} 
                  game={game} 
                  index={game._index}
                  favoriteTeam={favoriteTeam}
                  onToggleFavorite={handleToggleFavorite}
                />
              ))}
            </div>
          ))
        )}
      </div>
      
      {/* Favorite Team Selector */}
      {data && !favoriteTeam && (
        <div className="bg-yellow-50 px-3 py-2.5 border-t border-yellow-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-yellow-800 font-medium">
              <Star className="w-4 h-4 inline mr-1.5 text-yellow-600" />
              Pin your favorite team to see their games first
            </span>
            <select
              onChange={(e) => handleToggleFavorite(e.target.value)}
              className="text-sm bg-white border-2 border-yellow-400 rounded-lg px-3 py-1.5 text-gray-700 font-medium"
              data-testid="favorite-team-selector"
              defaultValue=""
            >
              <option value="" disabled>Select team</option>
              <option value="Jackson State">Jackson State</option>
              <option value="Alabama A&M">Alabama A&M</option>
              <option value="Alabama State">Alabama State</option>
              <option value="Alcorn State">Alcorn State</option>
              <option value="Arkansas-Pine Bluff">Arkansas-Pine Bluff</option>
              <option value="Bethune-Cookman">Bethune-Cookman</option>
              <option value="Florida A&M">Florida A&M</option>
              <option value="Grambling State">Grambling State</option>
              <option value="Mississippi Valley State">Mississippi Valley State</option>
              <option value="Prairie View A&M">Prairie View A&M</option>
              <option value="Southern">Southern</option>
              <option value="Texas Southern">Texas Southern</option>
            </select>
          </div>
        </div>
      )}
      
      {/* Clear Favorite Button - Enhanced Contrast */}
      {favoriteTeam && (
        <div className="bg-yellow-100 px-3 py-2 border-t border-yellow-300 flex items-center justify-between">
          <span className="text-sm text-[#00204E] font-semibold flex items-center gap-1.5">
            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
            Following: {favoriteTeam}
          </span>
          <button
            onClick={() => handleToggleFavorite(null)}
            className="text-sm text-red-700 hover:text-red-800 font-semibold bg-red-50 px-2 py-0.5 rounded"
            data-testid="clear-favorite-btn"
          >
            Clear
          </button>
        </div>
      )}
      
      {/* Footer - Enhanced Readability with full date/time */}
      {data && (
        <div className="bg-gray-50 px-3 py-2 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
            <Clock className="w-3 h-3" />
            Last updated: {new Date(data.updatedAt).toLocaleString('en-US', { 
              month: 'short',
              day: 'numeric',
              hour: 'numeric', 
              minute: '2-digit',
              timeZone: 'America/New_York',
              timeZoneName: 'short'
            })}
          </div>
          {error && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
              <AlertCircle className="w-3 h-3" />
              {error}
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}
